/**
 * User Service
 * 
 * Handles user management operations via Cognito backend API.
 */

import apiClient from './api';

/**
 * Unwrap the Lambda response envelope.
 * Lambda returns: { success, data: { ... } }
 * Axios wraps in: response.data
 * So actual payload is at response.data.data
 */
const unwrap = (response) => response.data?.data ?? response.data;

/**
 * Get list of all users
 */
export const getUsers = async (params = {}) => {
  try {
    const response = await apiClient.get('/users', { params });
    // Lambda returns { success, data: { data: [...], pagination: {...} } }
    const payload = unwrap(response);
    return {
      data: payload.data || [],
      pagination: payload.pagination || { page: 1, limit: 10, total: 0, totalPages: 1 }
    };
  } catch (error) {
    console.error('Failed to fetch users:', error);
    throw error;
  }
};

/**
 * Get user by ID
 */
export const getUserById = async (userId) => {
  try {
    const response = await apiClient.get(`/users/${userId}`);
    return unwrap(response);
  } catch (error) {
    console.error('Failed to fetch user:', error);
    throw error;
  }
};

/**
 * Create new user (admin only)
 */
export const createUser = async (userData) => {
  try {
    const response = await apiClient.post('/users', userData);
    return unwrap(response);
  } catch (error) {
    console.error('Failed to create user:', error);
    throw error;
  }
};

/**
 * Update user (admin only)
 */
export const updateUser = async (userId, userData) => {
  try {
    const response = await apiClient.put(`/users/${userId}`, userData);
    return unwrap(response);
  } catch (error) {
    console.error('Failed to update user:', error);
    throw error;
  }
};

/**
 * Disable user (admin only)
 */
export const disableUser = async (userId) => {
  try {
    const response = await apiClient.post(`/users/${userId}/disable`);
    return unwrap(response);
  } catch (error) {
    console.error('Failed to disable user:', error);
    throw error;
  }
};

/**
 * Enable user (admin only)
 */
export const enableUser = async (userId) => {
  try {
    const response = await apiClient.post(`/users/${userId}/enable`);
    return unwrap(response);
  } catch (error) {
    console.error('Failed to enable user:', error);
    throw error;
  }
};
